import { useState, useEffect, useRef } from 'react';
import BraintreeWebDropIn, { Dropin } from 'braintree-web-drop-in';
import { ExtendThreeDSecureOptions } from '@acorn-portal/shared/src/utils/types/extensions/braintree';
import * as Sentry from '@sentry/react';
import { toTitleCase } from '../utils/to-title-case';

interface BraintreeInstanceParams {
	clientToken?: string;
	amount?: string;
	isOptOut?: boolean;
	cardOnly?: boolean;
	noPayPal?: boolean;
	noMobileWallets?: boolean;
}

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));
const MAX_RETRIES = 10;

function getCompanyName(): string {
	const company = import.meta.env.VITE_AUTH_REALM;

	if (!company) {
		return 'Acorn Group';
	}

	const titleCase = toTitleCase(company);

	return `${titleCase} Insurance`;
}

const useBraintreeInstance = ({
	clientToken,
	amount,
	isOptOut,
	cardOnly,
	noPayPal,
	noMobileWallets,
}: BraintreeInstanceParams) => {
	const [instance, setInstance] = useState<Dropin>();
	const dropinInstance = useRef<Dropin>(null);
	const retry = useRef(0);

	const company = getCompanyName();

	const initBraintree = async () => {
		if (retry.current >= MAX_RETRIES) return;

		if (clientToken && amount) {
			try {
				if (!amount) throw new Error('Amount is missing');

				const dropinContainer = globalThis.document.querySelector('#dropin-container');
				if (dropinContainer) {
					dropinContainer.innerHTML = '';
				} else {
					throw new Error('No dropin-container found');
				}

				dropinInstance.current = await BraintreeWebDropIn.create({
					authorization: clientToken,
					container: '#dropin-container',
					threeDSecure: {
						amount,
						challengeRequested: true,
						collectDeviceData: true,
					} as ExtendThreeDSecureOptions,
					dataCollector: true,
					googlePay:
						cardOnly || noMobileWallets
							? undefined
							: {
									merchantId: import.meta.env.VITE_GOOGLE_MERCHANT_ID as string,
									transactionInfo: {
										currencyCode: 'GBP',
										countryCode: 'GB',
										totalPriceStatus: 'FINAL',
										totalPrice: amount,
										checkoutOption: 'COMPLETE_IMMEDIATE_PURCHASE',
									},
								},
					applePay:
						cardOnly || noMobileWallets
							? undefined
							: {
									displayName: company,
									paymentRequest: {
										total: {
											label: `Pay ${company}`,
											amount,
										},
										merchantCapabilities: ['supports3DS'],
										supportedNetworks: ['visa', 'masterCard'],
										currencyCode: 'GBP',
										countryCode: 'GB',
									},
								},
					paypal:
						cardOnly || noPayPal
							? undefined
							: {
									flow: isOptOut ? 'checkout' : 'vault',
									amount,
									currency: 'GBP',
								},
				});
				setInstance(dropinInstance.current);
			} catch (e) {
				if (retry.current < MAX_RETRIES) {
					await wait(retry.current * 100);
					retry.current++;
					initBraintree();
				}
				Sentry.captureException(e, {
					extra: {
						error: e,
						message: 'Error in useBraintree',
					},
				});
			}
		}
	};

	useEffect(() => {
		initBraintree();

		return () => {
			dropinInstance.current?.teardown();
		};
	}, [amount, clientToken]);

	return instance;
};

export default useBraintreeInstance;
